import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';

/**
 * DataGridComponent - A reusable grid component that displays items using a custom template
 * 
 * @description
 * This component provides a responsive grid layout that accepts any data array
 * and renders each item using a provided ng-template. The grid automatically
 * adjusts from 1 column on mobile to 3 columns on extra-large screens.
 * 
 * Supports both client-side and server-side (lazy) pagination modes.
 * 
 * @example
 * ```html
 * <!-- Client-side pagination -->
 * <app-data-grid [items]="users" [pagination]="true" [rows]="10">
 *   <ng-template #itemTemplate let-item>
 *     <div>{{ item.name }}</div>
 *   </ng-template>
 * </app-data-grid>
 * 
 * <!-- Server-side pagination -->
 * <app-data-grid 
 *   [items]="users" 
 *   [pagination]="true" 
 *   [lazy]="true"
 *   [totalRecords]="total"
 *   (pageChange)="loadPage($event)">
 *   <ng-template #itemTemplate let-item>
 *     <div>{{ item.name }}</div>
 *   </ng-template>
 * </app-data-grid>
 * ```
 */
@Component({
    selector: 'app-data-grid',
    standalone: true,
    imports: [CommonModule, PaginatorModule],
    templateUrl: './data-grid.component.html',
    styleUrl: './data-grid.component.scss'
})
export class DataGridComponent<T = any> implements OnInit, OnChanges {
    /**
     * Array of items to be displayed in the grid
     * @type {T[]}
     */
    @Input() items: T[] = [];

    /**
     * Grid column configuration for different breakpoints
     * @type {{ base?: number; sm?: number; md?: number; lg?: number; xl?: number; '2xl'?: number }}
     * @default { base: 1, sm: 2, md: 3, lg: 3, xl: 4, '2xl': 4 }
     */
    @Input() columns: { base?: number; sm?: number; md?: number; lg?: number; xl?: number; '2xl'?: number } = {
        base: 1,
        sm: 2,
        md: 3,
        lg: 3,
        xl: 4,
        '2xl': 4
    };

    /**
     * Gap size between grid items (in Tailwind spacing units)
     * @type {number}
     * @default 6
     */
    @Input() gap: number = 6;

    /**
     * Custom CSS classes for the container
     * @type {string}
     * @default '' (no container wrapper)
     */
    @Input() containerClass: string = '';

    /**
     * Custom CSS classes to append to the grid element
     * @type {string}
     */
    @Input() customGridClass: string = '';

    /**
     * Enable pagination for the grid
     * @type {boolean}
     * @default false
     */
    @Input() pagination: boolean = false;

    /**
     * Number of rows to display per page
     * @type {number}
     * @default 10
     */
    @Input() rows: number = 10;

    /**
     * Current page starting index
     * @type {number}
     * @default 0
     */
    @Input() first: number = 0;

    /**
     * Total number of records (for lazy/server-side pagination)
     * @type {number}
     * @default 0
     */
    @Input() totalRecords: number = 0;

    /**
     * Options for rows per page dropdown
     * @type {number[]}
     * @default [5, 10, 20, 50]
     */
    @Input() rowsPerPageOptions: number[] = [5, 10, 20, 50];

    /**
     * Whether to show the paginator
     * @type {boolean}
     * @default true
     */
    @Input() showPaginator: boolean = true;

    /**
     * Lazy loading mode (server-side pagination)
     * If true, parent component handles data loading for each page
     * If false, component handles pagination internally
     * @type {boolean}
     * @default false
     */
    @Input() lazy: boolean = false;

    /**
     * Event emitted when page changes (for lazy loading)
     * @event pageChange
     */
    @Output() pageChange = new EventEmitter<any>();

    /**
     * Template reference for rendering each grid item
     * The template receives the item as context using let-item
     */
    @ContentChild('itemTemplate', { read: TemplateRef }) itemTemplate!: TemplateRef<any>;

    /**
     * Internal state for current page start index
     * @type {number}
     */
    internalFirst: number = 0;

    /**
     * Items to display on current page
     * @type {T[]}
     */
    paginatedItems: T[] = [];

    /**
     * Lifecycle hook - Initialize component
     */
    ngOnInit() {
        this.internalFirst = this.first;
        this.updatePaginatedItems();
    }

    /**
     * Lifecycle hook - Handle input changes
     * @param changes - Changed properties
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes['items'] || changes['first']) {
            this.internalFirst = this.first;
            this.updatePaginatedItems();
        }
    }

    /**
     * Updates paginated items based on pagination mode
     * @private
     */
    private updatePaginatedItems() {
        if (this.lazy) {
            // Lazy mode: parent provides pre-sliced data
            this.paginatedItems = this.items;
        } else if (this.pagination) {
            // Client-side: slice items locally
            this.paginatedItems = this.items.slice(
                this.internalFirst,
                this.internalFirst + this.rows
            );
        } else {
            // No pagination: show all items
            this.paginatedItems = this.items;
        }
    }

    /**
     * Handles page change event from paginator
     * @param event - Page change event from PrimeNG paginator
     */
    onPageChange(event: any) {
        this.internalFirst = event.first;

        if (this.lazy) {
            // Emit to parent for server-side loading
            this.pageChange.emit(event);
        } else {
            // Handle client-side pagination
            this.updatePaginatedItems();
        }
    }

    /**
     * Gets total records for paginator
     * @returns {number} Total number of records
     */
    get displayTotalRecords(): number {
        return this.lazy ? this.totalRecords : this.items.length;
    }

    /**
     * Determines if paginator should be shown
     * @returns {boolean} True if paginator should be displayed
     */
    get shouldShowPaginator(): boolean {
        return this.showPaginator &&
            this.pagination &&
            this.displayTotalRecords > 0;
    }

    /**
     * Generates dynamic grid column classes based on the columns configuration
     * @returns {string} Tailwind CSS classes for grid columns
     */
    getGridClasses(): string {
        const classes: string[] = ['auto-rows-min', 'items-stretch', 'w-full'];

        if (this.columns.base) {
            classes.push(`grid-cols-${this.columns.base}`);
        }
        if (this.columns.sm) {
            classes.push(`sm:grid-cols-${this.columns.sm}`);
        }
        if (this.columns.md) {
            classes.push(`md:grid-cols-${this.columns.md}`);
        }
        if (this.columns.lg) {
            classes.push(`lg:grid-cols-${this.columns.lg}`);
        }
        if (this.columns.xl) {
            classes.push(`xl:grid-cols-${this.columns.xl}`);
        }
        if (this.columns['2xl']) {
            classes.push(`2xl:grid-cols-${this.columns['2xl']}`);
        }

        classes.push(`gap-${this.gap}`);

        if (this.customGridClass) {
            classes.push(this.customGridClass);
        }

        return classes.join(' ');
    }
}

